import { Component, OnDestroy, OnInit } from '@angular/core';
import { ArhivskaKnjigaClient, GetKategorijeDokumentarnogMaterijalaQuery, KategorijaDokMaterijalaDto, KategorijaTableDto, PaginatedResultOfKategorijaTableDto } from '@kodit/core/data-api';
import { AlertService, ConfigService, LocationService, SharedService } from '@kodit/core/services';
import { ActionClass, ActionType, DynamicDialogConfig, DynamicDialogRef, DynamicDialogService, TableConfig, TableType } from '@kodit/core/shared-ui';
import { Store } from '@ngrx/store';
import { setFilter } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.actions';
import { selectFilterByKey } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.selectors';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { ConfirmationService, LazyLoadEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { KategorijeMaterijalaFormComponent } from '../kategorije-materijala-form/kategorije-materijala-form.component';
import { ArhivskaKnjigaService } from '../../arhivska-knjiga-service';

@Component({
  selector: 'kodit-kategorije-materijala-table',
  templateUrl: './kategorije-materijala-table.component.html',
  styleUrls: ['./kategorije-materijala-table.component.scss']
})
export class KategorijeMaterijalaTableComponent implements OnInit, OnDestroy {

  paginatedData: PaginatedResultOfKategorijaTableDto;
  tableConfig!: TableConfig;
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);

  kategorijeTable: KategorijaTableDto[] | undefined;

  filters: GetKategorijeDokumentarnogMaterijalaQuery;

  skipFirst: number | undefined = 0;
  numberOfRowsDisplayed: number | undefined = 0;
  isFirstLoad = true;
  isLoading = true;
  private _isBusy = false;
  showImportSection = false;
  kategorijaId: number | undefined;
  customUpload: boolean = true;
  showProgressBar :boolean;

  private _subs: Subscription = new Subscription();

  constructor(
    private _client: ArhivskaKnjigaClient,
    private _storage: Store,
    private _locationService: LocationService,
    private _configService: ConfigService,
    private _dialogService: DynamicDialogService,
    private _arhivskaService: ArhivskaKnjigaService,
    private _alertService: AlertService,
    private _sharedService: SharedService,
    private _confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this._subs.add(
      this._storage.select(selectFilterByKey('KATEGORIJE_MATERIJALA') as any).subscribe((data) => {
        if (data) {
          this.filters = JSON.parse(data as string);
          this.skipFirst = this.filters.pageNumber ?? 0;
          this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
        } else {
          this.filters = new GetKategorijeDokumentarnogMaterijalaQuery({
            pageNumber: this.skipFirst,
            pageSize: this.numberOfRowsDisplayed
          });
        }
        this._loadTable();
      })
    );
    this.setTableConfig();
    this.skipFirst = 0;
    this.filters.pageNumber = 0;
    this._updateStorage();
    this._loadTable();
  }


  private _loadTable() {

    if (this._isBusy) {
      return;
    }

    this._configService.setIsBusy = true;
    this._isBusy = true;

    if (!this.filters) {
      this.filters = new GetKategorijeDokumentarnogMaterijalaQuery({
        pageNumber: this.skipFirst,
        pageSize: this.numberOfRowsDisplayed
      });
    }

    this.filters.pageNumber = this.skipFirst;
    this.filters.pageSize = this.numberOfRowsDisplayed;

    this.isLoading = true;
    this._subs.add(
      this._client.getKategorijeDokumentatnogMaterijala(this.filters)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this._isBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe((res) => {
          this.paginatedData = res;
          this.kategorijeTable = res.data;
        })
    );
  }

  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      tableHeader: 'Kategorije dokumentarnog materijala',
      isLazy: true,
      lazyCallbackFunction: (event: LazyLoadEvent) => {
        if (!this.isFirstLoad) {
          this.skipFirst = event.first;
          this.numberOfRowsDisplayed = event.rows;
        }
        this._updateFilterDataAndReload();
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      columns: [
        {
          field: 'nazivGrupe',
          header: 'Naziv grupe',
          type: 'text',
          styleClass: 'w-30rem'
        },
        {
          field: 'naziv',
          header: 'Naziv kategorije',
          type: 'text',
          styleClass: 'w-30rem'
        },
        {
          field: 'rokCuvanja',
          header: 'Rok čuvanja',
          type: 'number'
        },
      ],
      headerActions: [
        {
          type: ActionType.CREATE,
          hasAccessTooltip: 'Unos nove kategorije dokumentarnog materijala',
          callback: () => {
            this.dialogConfig.header = 'Unos nove kategorije dokumentarnog materijala';
            this.dialogConfig.data = { id: null };
            this.openDialog(this.dialogConfig);
          },
        },
        {
          type: ActionType.CUSTOM,
          actionClass: ActionClass.OUTLINED,
          label: 'Preuzmi datoteku',
          icon: 'fa-solid fa-download',
          hasAccessTooltip: 'Preuzmite šablon za listu kategorija',
          callback: () => {
            this._preuzmiSablon();
          }
        },
        {
          type: ActionType.CUSTOM,
          actionClass: ActionClass.OUTLINED,
          label: 'Otpremi datoteku',
          icon: 'fa-regular fa-upload',
          hasAccessTooltip: 'Otpremite šablon liste kategorija',
          callback: () => {
            this.showImportSection = true
          }
        }
      ],
      rowActions: [
        {
          type: ActionType.EDIT,
          hasAccessTooltip: 'Izmenite naziv i rok čuvanja kategorije',
          callback: (index: number) => {
            const kategorija = this._getPaginatedItem(index);
            this.dialogConfig.header = 'Izmena kategorije dokumentarnog materijala';
            this.dialogConfig.data = {
              id: kategorija.id,
              naziv: kategorija.naziv,
              rokCuvanja: kategorija.rokCuvanja,
            };
            this.openDialog(this.dialogConfig);
          },
        },
        {
          type: ActionType.DELETE,
          hasAccessTooltip: 'Obrišite kategoriju',
          callback: (index: number) => {
            const kategorija = this._getPaginatedItem(index);

            if (kategorija.imaPovezaneUpise) {
              this._alertService.addWarnMsg(
                `Kategorija "${kategorija.naziv}" se ne može obrisati jer postoje upisi (u arhivskoj knjizi ili delovodniku) povezani sa ovom kategorijom.`
              );
              return;
            }

            this._confirmationService.confirm({
              header: 'Potvrda brisanja',
              message: `Da li želite da obrišete kategoriju "${kategorija.naziv}"?`,
              acceptLabel: 'Obriši',
              rejectLabel: 'Odustani',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this._deleteKategorija(kategorija.id!);
              },
            });
          },
        },
      ],
    });
  }

  private _getPaginatedItem(index: number): KategorijaTableDto {
    return this.kategorijeTable![index - this.skipFirst!];
  }

  private _deleteKategorija(id: number) {
    this._sharedService.displayLoadingScreen(true, 'Brisanje kategorije...');

    this._subs.add(
      this._client
        .deleteKategorijaDokumentarnogMaterijala(id)
        .pipe(
          finalize(() => {
            this._sharedService.resetLoadingScreen();
          })
        )
        .subscribe((res) => {
          if (res.succeeded) {
            this._alertService.addSuccessMsg(res.messages![0]);
            this._loadTable();
          } else {
            this._alertService.addFailedMsg(res.messages?.[0] ?? 'Brisanje nije uspelo');
          }
        })
    );
  }

  private _updateFilterDataAndReload() {
    if (
      (this.skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this.skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      this._updateStorage();
    }
  }
  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: 'KATEGORIJE_MATERIJALA',
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _preuzmiSablon() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute(
      'href',
      '/assets/podesavanje/import_files/kategorije_dokumentarnog_sablon.xlsx'
    );
    link.setAttribute('download', 'kategorije_dokumentarnog_sablon.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  otpremiDatoteku(event: any) {
    if(this._arhivskaService.getGrupaKategorijaIdValue() == 0) {
      this._alertService.addWarnMsg("Morate da izaberete grupu kategorija");
      return;
    }
    this.kategorijaId = this._arhivskaService.getGrupaKategorijaIdValue();
    this.showProgressBar = true;
    this._subs.add(
      this._client
        .importKategorijeSablon(this.kategorijaId, {
          data: event.files[0],
          fileName: event.files[0].name,
        })
        .pipe(
            finalize(() => {
              setTimeout(() => {
                this.showProgressBar = false;
                this.showImportSection = false;
              }, 500);
            })
        )
        .subscribe((res) => {
          if (res.succeeded) {
            this._alertService.addSuccessMsg(res.messages![0]);
            this._arhivskaService.setStep01Completed = true;
            this._arhivskaService.setSledeciKorakEnabled = true;
            this._arhivskaService.resetDropdown();
            this._loadTable();
          }
        })
    );
  }

  close(): void {
    this.showImportSection = false;
  }

  openDialog(config: DynamicDialogConfig) {
    const ref: DynamicDialogRef = this._dialogService.open(
     KategorijeMaterijalaFormComponent,
     config
    );

    this._subs.add(
      ref.onClose.subscribe((dto: any) => {
        if (dto) {
          this._loadTable();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

}


